import { Component } from '@theme/component'
import { SlideshowSelectEvent, ThemeEvents } from '@theme/events'
import { morph } from '@theme/morph'
import { ProductCardMediaHover } from '@theme/product-card-media-hover'
import { isDesktop, mediaQueryLarge } from '@theme/use-device-detection'
import { useIntersection } from '@theme/use-intersection'
import { debounce, requestYieldCallback } from '@theme/utils'

const ProductCardConfig = {
  interactiveClickSelector:
    'button, input, label, select, textarea, summary, [role="button"], [tabindex]:not([tabindex^="-"])',
  revealMarginPx: 120
}

const cardRevealObserver =
  typeof window !== 'undefined'
    ? useIntersection({
        rootMargin: `${ProductCardConfig.revealMarginPx}px 0px`,
        threshold: 0.01
      })
    : null

export class ProductCard extends Component {
  requiredRefs = ['productCardLink']

  #mediaHover = new ProductCardMediaHover(this)
  #revealUnobserve = null

  get productPageUrl() {
    return this.refs.productCardLink.href
  }

  getSelectedVariantId() {
    const checkedInput = this.querySelector(
      'input[type="radio"]:checked[data-variant-id]'
    )

    return checkedInput?.dataset.variantId || null
  }

  getProductCardLink() {
    return this.refs.productCardLink || null
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl)
  }

  #navigateToURL = (event, url) => {
    const shouldOpenInNewTab =
      event instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)

    if (shouldOpenInNewTab) {
      event.preventDefault()
      window.open(url.href, '_blank')
      return
    }
    window.location.href = url.href
  }

  connectedCallback() {
    super.connectedCallback()

    const link = this.refs.productCardLink
    if (!(link instanceof HTMLAnchorElement)) {
      throw new Error('Product card link not found')
    }
    this.#handleQuickAdd()
    this.#mediaHover.setup()
    this.#setupScrollReveal()

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate)
    this.addEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected
    )
    this.addEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleSlideshowSelect
    )
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd)

    this.addEventListener('click', this.navigateToProduct)

    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage()
      }
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.#mediaHover.teardown()
    this.#teardownScrollReveal()
    this.removeEventListener('click', this.navigateToProduct)
  }

  #setupScrollReveal() {
    this.#teardownScrollReveal()

    if (this.dataset.cardLazy !== 'true') {
      this.dataset.cardReveal = 'visible'
      return
    }

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      this.dataset.cardReveal = 'visible'
      return
    }

    this.dataset.cardReveal = 'pending'

    if (!cardRevealObserver) {
      this.dataset.cardReveal = 'visible'
      return
    }

    this.#revealUnobserve = cardRevealObserver.observe(this, (entry) => {
      if (!entry.isIntersecting) {
        return
      }

      this.dataset.cardReveal = 'visible'
      this.#teardownScrollReveal()
    })
  }

  #teardownScrollReveal() {
    if (typeof this.#revealUnobserve === 'function') {
      this.#revealUnobserve()
    }
    this.#revealUnobserve = null
  }

  #preloadNextPreviewImage() {
    const currentSlide =
      this.refs.slideshow?.slides?.[this.refs.slideshow?.current]
    currentSlide?.nextElementSibling
      ?.querySelector('img[loading="lazy"]')
      ?.removeAttribute('loading')
  }

  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler)
    this.removeEventListener('focusin', this.#fetchProductPageHandler)

    if (isDesktop()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler)
      this.addEventListener('focusin', this.#fetchProductPageHandler)
    }
  }

  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id)
    }
  }

  #handleVariantUpdate = (event) => {
    event.stopPropagation()

    this.updatePrice(event)
    this.#isUnavailableVariantSelected(event)
    this.#updateProductUrl(event)
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl)

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html)
    }

    this.#updateVariantImages()
    this.#previousSlideIndex = null

    this.removeAttribute('data-no-swatch-selected')

    this.#mediaHover.setup()
  }

  updatePrice(event) {
    const priceContainer = this.querySelectorAll(
      `product-price [ref='priceContainer']`
    )[1]
    const newPriceElement = event.detail.data.html.querySelector(
      `product-price [ref='priceContainer']`
    )

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement)
    }
  }

  #updateProductUrl(event) {
    const anchorElement =
      event.detail.data.html?.querySelector('product-card a')
    if (anchorElement instanceof HTMLAnchorElement) {
      if (anchorElement.getAttribute('href')?.trim() === '') {
        return
      }

      const productUrl = anchorElement.href
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs

      productCardLink.href = productUrl
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl
      }
    }
  }

  #isUnavailableVariantSelected(event) {
    const allVariants = event.detail.data.html.querySelectorAll('input:checked')

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true')
    }
  }

  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button')

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable
    }
  }

  #updateVariantImages() {
    const { slideshow } = this.refs
    if (!this.variantPicker?.selectedOption) {
      return
    }

    const selectedImageId =
      this.variantPicker?.selectedOption.dataset.optionMediaId

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) {
          continue
        }

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId
      }

      slideshow.select({ id: selectedImageId }, undefined, {
        animate: false
      })
    }
  }

  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]')
  }

  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component')
  }

  #previousSlideIndex = null

  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index
    }
  }

  previewVariant(id) {
    const { slideshow } = this.refs
    if (!slideshow) return
    this.resetVariant.cancel()
    slideshow.select({ id }, undefined, { animate: false })
  }

  previewImage(event) {
    if (event.pointerType !== 'mouse') return

    const { slideshow } = this.refs
    if (!slideshow) return

    this.resetVariant.cancel()

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, {
        animate: false
      })
    } else {
      slideshow.next(undefined, { animate: false })
      setTimeout(() => this.#preloadNextPreviewImage())
    }
  }

  resetImage(event) {
    if (event.pointerType !== 'mouse') return

    if (!this.variantPicker) {
      const { slideshow } = this.refs
      slideshow?.previous(undefined, { animate: false })
      return
    }

    this.#resetVariant()
  }

  #resetVariant = () => {
    const { slideshow } = this.refs

    if (!slideshow) {
      return
    }

    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId
      if (id) {
        slideshow.select({ id }, undefined, { animate: false })
        return
      }
    }

    const initialSlide = slideshow.initialSlide
    const slideId = initialSlide?.getAttribute('slide-id')
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false })
      return
    }

    slideshow.previous(undefined, { animate: false })
  }

  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) {
      return
    }

    if (this.hasAttribute('data-no-navigation')) {
      return
    }

    const interactiveElement = event.target.closest(
      ProductCardConfig.interactiveClickSelector
    )

    if (interactiveElement) {
      return
    }

    const link = this.refs.productCardLink
    if (!link.href) {
      return
    }
    const linkURL = new URL(link.href)

    const productCardAnchor = link.getAttribute('id')
    if (!productCardAnchor) {
      return
    }

    const url = new URL(window.location.href)
    const parent = this.closest('li')
    url.hash = productCardAnchor
    if (parent?.dataset.page) {
      url.searchParams.set('page', parent.dataset.page)
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString())
      })
    }

    if (!event.target.closest('a')) {
      this.#navigateToURL(event, linkURL)
    }
  }

  resetVariant = debounce(this.#resetVariant, 100)
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard)
}
